import { FC } from "react";
import styled from "@emotion/styled";
import { InterstitialOverlay } from "@/components/overlay";
import { AuthCardGoogleButtonBase } from "@/components/auth/google/auth-card-google-button-base";
import { GuestSharingAccountInfo } from "@/store/guest-account/types";
import { MDSButton, MDSButtonVariant, MDSLogo, MDSLogoKind } from "@/mds";

interface GuestModeGreetingInterstitialOverlayContentProps {
  isOpen: boolean;
  sharingAccount?: GuestSharingAccountInfo;
  sharingNoteTitle: string;
  onSignUp: () => void;
  onClose: () => void;
}

export const GuestModeGreetingInterstitialOverlayContent: FC<
  GuestModeGreetingInterstitialOverlayContentProps
> = ({ isOpen, sharingAccount, sharingNoteTitle, onSignUp, onClose }) => {
  return (
    <InterstitialOverlay isOpen={isOpen} handleClose={onClose}>
      <StyledContainer>
        <StyledLogo kind={MDSLogoKind.Arrows} />
        <StyledSmallerGap>
          <StyledHeader>
            {sharingAccount?.profileDisplayName ?? "Someone"} shared &ldquo;
            {sharingNoteTitle}&rdquo; with you
          </StyledHeader>
          <MDSButton variant={MDSButtonVariant.Contrast} onClick={onClose}>
            Continue as guest
          </MDSButton>
        </StyledSmallerGap>
        <StyledDivider>
          <StyledHr />
        </StyledDivider>
        <StyledSmallerGap>
          <StyledInfo>
            Or, sign up to access all of Mem—the AI notes app that keeps you organized
          </StyledInfo>
          <AuthCardGoogleButtonBase label="Sign up with Google" onClick={onSignUp} />
        </StyledSmallerGap>
      </StyledContainer>
    </InterstitialOverlay>
  );
};

const StyledContainer = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.colors.surface.pane};
  border-color: ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  border-style: solid;
  border-width: 1px;
  box-shadow: ${({ theme }) => theme.shadows.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  flex-direction: column;
  gap: 24px;
  justify-content: flex-start;
  letter-spacing: ${({ theme }) => theme.lineSpacings["header-l"]};
  line-height: 32px;
  max-width: 640px;
  padding: 40px;
  width: 100%;

  @media (min-width: 672px) and (min-height: 632px) {
    justify-content: center;
  }
`;

const StyledSmallerGap = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StyledHeader = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  font-size: 24px;
  font-weight: 700;
  justify-content: center;
  letter-spacing: ${({ theme }) => theme.lineSpacings["header-l"]};
  line-height: 32px;
  text-align: center;
`;

const StyledDivider = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  padding: 4px 0px;
  width: 100%;
`;

const StyledHr = styled.div`
  background: ${({ theme }) => theme.colors.border.default};
  height: 1px;
  width: 100%;
`;

const StyledInfo = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: 400;
  font-size: 16px;
  letter-spacing: ${({ theme }) => theme.lineSpacings["header-m"]};
  line-height: 24px;
  text-align: center;
`;

const StyledLogo = styled(MDSLogo)`
  height: 56px;
  width: 56px;
`;
